import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { getFirebaseServices } from "@/lib/firebase";
import { hasActiveAccess } from "@/lib/access";
import { toggleLocale, translate } from "@/i18n";
import { useLocale } from "@/i18n/useLocale";

const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

const IMMEDIATE_AUTH_PATHS = [
  "/app",
  "/auth",
  "/login",
  "/registro",
  "/recuperar",
  "/restablecer",
  "/verificar",
  "/panel-asesor",
  "/pago",
  "/payment",
];

export const shouldInitializeFirebaseImmediately = (pathname) =>
  IMMEDIATE_AUTH_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export const PUBLIC_AUTH_IDLE_TIMEOUT = 5000;
export const PRIVATE_AUTH_RESOLUTION_TIMEOUT = 10000;

const verificationActionSettings = () => ({
  url: `${window.location.origin}/verificar`,
});

export const clearAccountCache = () => {
  ["ef_token", "ef_refresh", "ef_diag_answers", "ef_diag_result"].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const lang = useLocale();
  const authActionRef = useRef(false);

  const persistPendingDiagnosis = useCallback(async () => {
    try {
      const raw = localStorage.getItem("ef_diag_answers");
      if (!raw) return;
      const answers = JSON.parse(raw);
      let previewToken;
      try {
        previewToken = JSON.parse(localStorage.getItem("ef_diag_result") || "null")?.previewToken;
      } catch {}
      await client.post("/diagnosis", { answers, ...(previewToken ? { previewToken } : {}) });
      localStorage.removeItem("ef_diag_answers");
      localStorage.removeItem("ef_diag_result");
    } catch {}
  }, []);

  const syncFirebaseProfile = useCallback(async (firebaseUser, metadata = {}, options = {}) => {
    const idToken = await firebaseUser.getIdToken(true);
    const { data } = await client.post("/auth/firebase", {
      id_token: idToken,
      ...metadata,
    });
    const appUser = data.user;
    if (appUser && !hasActiveAccess(appUser)) {
      try {
        const { data: rec } = await client.post("/payments/reconcile");
        if (rec?.plan && rec.plan !== appUser.plan) appUser.plan = rec.plan;
        if (rec?.workspace_access) appUser.workspace_access = rec.workspace_access;
        if (rec?.capabilities) appUser.capabilities = rec.capabilities;
      } catch {}
    }
    if (options.commit !== false) setUser(appUser);
    return { ...appUser, registrationCreated: Boolean(data.created) };
  }, []);

  const refreshUser = useCallback(async () => {
    const { auth, authApi } = await getFirebaseServices();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      await authApi.reload(firebaseUser);
      return await syncFirebaseProfile(firebaseUser);
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [syncFirebaseProfile]);

  useEffect(() => {
    localStorage.removeItem("ef_token");
    localStorage.removeItem("ef_refresh");

    let active = true;
    let unsubscribe;
    let idleId;
    let timeoutId;
    let authResolutionTimeoutId;
    let authStarted = false;
    let initialAuthResolved = false;

    const finishInitialAuth = () => {
      if (initialAuthResolved) return;
      initialAuthResolved = true;
      if (authResolutionTimeoutId !== undefined) {
        window.clearTimeout(authResolutionTimeoutId);
        authResolutionTimeoutId = undefined;
      }
      setLoading(false);
    };

    const cancelDeferredStart = () => {
      if (idleId !== undefined && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
        idleId = undefined;
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const startAuthListener = async () => {
      if (!active || authStarted) return;
      authStarted = true;
      cancelDeferredStart();
      authResolutionTimeoutId = window.setTimeout(() => {
        if (!active || initialAuthResolved) return;
        setUser(null);
        finishInitialAuth();
      }, PRIVATE_AUTH_RESOLUTION_TIMEOUT);
      try {
        const { auth, authApi } = await getFirebaseServices();
        if (!active || initialAuthResolved) return;
        unsubscribe = authApi.onAuthStateChanged(auth, async (firebaseUser) => {
          if (authActionRef.current) return;
          const resolvingInitialAuth = !initialAuthResolved;
          if (!firebaseUser) {
            setUser(null);
            finishInitialAuth();
            return;
          }
          try {
            const syncedUser = await syncFirebaseProfile(
              firebaseUser, {}, { commit: false }
            );
            if (!active || (resolvingInitialAuth && initialAuthResolved)) return;
            const appUser = { ...syncedUser };
            delete appUser.registrationCreated;
            setUser(appUser);
          } catch {
            if (active && !(resolvingInitialAuth && initialAuthResolved)) setUser(null);
          } finally {
            if (active && resolvingInitialAuth) finishInitialAuth();
          }
        });
      } catch {
        if (active) finishInitialAuth();
      }
    };

    const scheduleDeferredAuth = () => {
      if (!active || authStarted) return;
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(startAuthListener, { timeout: PUBLIC_AUTH_IDLE_TIMEOUT });
      } else {
        timeoutId = window.setTimeout(startAuthListener, PUBLIC_AUTH_IDLE_TIMEOUT);
      }
    };

    const startAuthForPrivateNavigation = (event) => {
      const anchor = typeof event.target?.closest === "function" ? event.target.closest("a[href]") : null;
      if (!anchor) return;
      try {
        const destination = new URL(anchor.href, window.location.href);
        if (destination.origin !== window.location.origin || !shouldInitializeFirebaseImmediately(destination.pathname)) return;
        startAuthListener();
      } catch {}
    };

    if (shouldInitializeFirebaseImmediately(window.location.pathname)) {
      startAuthListener();
    } else {
      document.addEventListener("click", startAuthForPrivateNavigation, true);
      if (document.readyState === "complete") {
        scheduleDeferredAuth();
      } else {
        window.addEventListener("load", scheduleDeferredAuth, { once: true });
      }
    }

    return () => {
      active = false;
      document.removeEventListener("click", startAuthForPrivateNavigation, true);
      window.removeEventListener("load", scheduleDeferredAuth);
      cancelDeferredStart();
      if (authResolutionTimeoutId !== undefined) {
        window.clearTimeout(authResolutionTimeoutId);
      }
      unsubscribe?.();
    };
  }, [syncFirebaseProfile]);

  const login = async (email, password) => {
    authActionRef.current = true;
    setLoading(true);
    try {
      const { auth, authApi } = await getFirebaseServices();
      const result = await authApi.signInWithEmailAndPassword(auth, email.trim(), password);
      const appUser = await syncFirebaseProfile(result.user);
      await persistPendingDiagnosis();
      return appUser;
    } finally {
      authActionRef.current = false;
      setLoading(false);
    }
  };

  const register = async (payload) => {
    if ((payload.password || "").length < 8) {
      const error = new Error("weak-password");
      error.code = "auth/weak-password";
      throw error;
    }
    authActionRef.current = true;
    setLoading(true);
    try {
      const { auth, authApi } = await getFirebaseServices();
      const result = await authApi.createUserWithEmailAndPassword(auth, payload.email.trim(), payload.password);
      await authApi.updateProfile(result.user, { displayName: payload.name.trim() });
      try {
        await authApi.sendEmailVerification(result.user, verificationActionSettings());
      } catch (error) {
        console.warn("Firebase verification email could not be sent", error?.code || "unknown");
      }
      const appUser = await syncFirebaseProfile(result.user, {
        name: payload.name,
        region: payload.region || "España",
        language: payload.language || lang,
        marketingConsent: Boolean(payload.marketingConsent),
        termsAccepted: Boolean(payload.termsAccepted),
      });
      await persistPendingDiagnosis();
      return appUser;
    } finally {
      authActionRef.current = false;
      setLoading(false);
    }
  };

  const loginWithGoogle = async (metadata = {}) => {
    authActionRef.current = true;
    setLoading(true);
    try {
      const { auth, authApi, googleProvider } = await getFirebaseServices();
      const result = await authApi.signInWithPopup(auth, googleProvider);
      const appUser = await syncFirebaseProfile(result.user, { language: lang, ...metadata });
      await persistPendingDiagnosis();
      return appUser;
    } finally {
      authActionRef.current = false;
      setLoading(false);
    }
  };

  const logout = async (options = {}) => {
    try {
      const { auth, authApi } = await getFirebaseServices();
      await authApi.signOut(auth);
    } finally {
      if (options?.clearAccountData) {
        clearAccountCache();
      } else {
        localStorage.removeItem("ef_token");
        localStorage.removeItem("ef_refresh");
      }
      setUser(null);
    }
  };

  const toggleLang = toggleLocale;
  const t = (key, values) => translate(key, values, lang);

  return (
    <AppCtx.Provider value={{ user, setUser, loading, login, register, loginWithGoogle, logout, refreshUser, lang, toggleLang, t }}>
      {children}
    </AppCtx.Provider>
  );
}
